import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiArrowRight, FiGrid } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';
import { categories } from '../data/categories';
import { templates } from '../data/templates';
import { applyTemplateToDraft } from '../utils/templateEngine';
import Badge from '../components/Badge';
import SectionTitle from '../components/SectionTitle';

const TemplateCard = ({ template, onSelect }) => {
  const categoryLabel = categories.find(c => c.id === template.applyTo)?.label || 'General';

  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300"
    >
      <div className="p-6 flex-grow">
        <div className="flex items-center justify-between mb-3">
          <Badge className="bg-indigo-100 text-indigo-800">{categoryLabel}</Badge>
          <FiGrid className="h-5 w-5 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{template.name}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{template.description}</p>
      </div>
      <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-100">
        <button
          onClick={() => onSelect(template)}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
        >
          Use Template
          <FiArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

function Categories() {
  const { projectDraft, dispatch } = useAppContext();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleSelectTemplate = (template) => {
    const newDraft = applyTemplateToDraft(template, projectDraft);
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: newDraft });
    console.log(`Template "${template.name}" applied.`);
    navigate('/start-building');
  };

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.applyTo === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle 
        title="Project Templates" 
        subtitle="Choose from our curated collection of templates to jumpstart your project." 
      />
      
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-grow">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </select>
      </div>

      {filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTemplates.map(template => (
            <TemplateCard 
              key={template.id} 
              template={template} 
              onSelect={handleSelectTemplate} 
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiGrid className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg">No templates found matching your criteria.</p>
          <p className="text-gray-400 text-sm mt-2">Try adjusting your search or category filter.</p>
        </div>
      )}
    </motion.div>
  );
}

export default Categories;